import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { toast } from 'sonner';

import { fleetImportApi } from '../../lib/fleetImportApi';
import { messageErreur } from '../../lib/erreurs';
import { formatNombre } from '../../lib/formats';
import { useDebouncedValue } from '../../hooks/use-debounced-value';
import { BarrePagination } from '../ui/pagination-bar';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { StatutBadge } from '../ui/StatutBadge';

/**
 * Brouillon ligne par ligne: trois états (prête, à vérifier avec la raison,
 * refusée avec la phrase du refus), cellules corrigeables, lignes décochables.
 * Chaque correction est renvoyée au serveur, qui recalcule le brouillon.
 *
 * Les colonnes sont celles du travail quand il les donne (`draft_columns`, liste
 * des camions d'un lot) ; sinon celles de la flotte, ci-dessous. Une plaque déjà
 * dans le lot (`in_lot`) n'est ni refusée ni cochable.
 */

// Taille de page de départ; l'utilisateur choisit 20, 50 ou 100 dans la barre.
const TAILLE = 50;

const COLONNES = {
  trucks: [
    { cle: 'plate', titre: 'Plaque tracteur', valeur: (v) => v.plate },
    { cle: 'tank_plate', titre: 'Plaque citerne', valeur: (v) => v.tank_plate },
    { cle: 'capacity', titre: 'Capacité (L)', droite: true, valeur: (v) => v.capacity, affichage: (v) => formatNombre(v.capacity) },
    { cle: 'brand', titre: 'Marque', valeur: (v) => v.brand },
    { cle: 'year', titre: 'Année', droite: true, valeur: (v) => v.year },
    {
      cle: 'ownership',
      titre: 'Propriété',
      valeur: (v) => (v.ownership_type === 'LOUE' ? 'Loué' : 'Propre'),
    },
  ],
  drivers: [
    { cle: 'full_name', titre: 'Nom', valeur: (v) => v.full_name },
    { cle: 'phone', titre: 'Téléphone', valeur: (v) => v.phone },
    { cle: 'license_number', titre: 'Numéro de permis', valeur: (v) => v.license_number },
  ],
};

const NATURES_LIBELLES = { trucks: 'Camions', drivers: 'Chauffeurs', lot_trucks: 'Camions du lot' };
const NON_COCHABLES = ['refused', 'in_lot'];

/** Colonnes d'une nature : celles que donne le travail, sinon celles de la flotte. */
const colonnesDe = (job, nature) => {
  const donnees = job.draft_columns?.[nature];
  if (!donnees) return COLONNES[nature] || [];
  return donnees.map((colonne) => ({
    cle: colonne.key,
    titre: colonne.label,
    droite: colonne.align === 'right',
    valeur: (v) => v[colonne.key],
    affichage: colonne.format === 'number' ? (v) => formatNombre(v[colonne.key]) : undefined,
  }));
};

const texte = (valeur) => (valeur === null || valeur === undefined || valeur === '' ? '—' : String(valeur));

const CelluleEditable = ({ valeur, affichage, droite, disabled, titre, onSave }) => {
  const [edition, setEdition] = useState(false);
  const [brouillon, setBrouillon] = useState('');
  const fini = useRef(false);

  const ouvrir = () => {
    if (disabled) return;
    fini.current = false;
    setBrouillon(valeur === null || valeur === undefined ? '' : String(valeur));
    setEdition(true);
  };
  const valider = () => {
    if (fini.current) return;
    fini.current = true;
    setEdition(false);
    const nouveau = brouillon.trim();
    if (nouveau !== (valeur === null || valeur === undefined ? '' : String(valeur))) onSave(nouveau);
  };

  if (edition) {
    return (
      <Input
        autoFocus
        aria-label={titre}
        value={brouillon}
        onChange={(event) => setBrouillon(event.target.value)}
        onBlur={valider}
        onKeyDown={(event) => {
          if (event.key === 'Enter') valider();
          if (event.key === 'Escape') {
            fini.current = true;
            setEdition(false);
          }
        }}
        className={`h-8 min-w-[6rem] text-sm ${droite ? 'text-right tabular-nums' : ''}`}
      />
    );
  }
  return (
    <button
      type="button"
      onClick={ouvrir}
      disabled={disabled}
      aria-label={`Corriger : ${titre}`}
      className={`w-full rounded px-1 py-0.5 text-sm hover:bg-muted/60 disabled:cursor-default disabled:hover:bg-transparent ${
        droite ? 'text-right tabular-nums' : 'text-left'
      }`}
    >
      {affichage ?? texte(valeur)}
    </button>
  );
};

/** Les cellules d'une ligne refusée se corrigent quand le serveur le dit (donnée manquante à saisir). */
const cellulesFigees = (ligne) => ligne.state === 'in_lot' || (ligne.state === 'refused' && !ligne.editable);

/**
 * `api` : les appels du brouillon (lignes, correction, sélection). Par défaut ceux de l'import de flotte ;
 * l'import intelligent passe les siens (mêmes noms, autres routes).
 */
const TableauBrouillon = ({ job, onJob, disabled, api = fleetImportApi }) => {
  const resume = job.summary || {};
  const natures = job.draft_columns ? Object.keys(job.draft_columns) : ['trucks', 'drivers'];
  const [nature, setNature] = useState(() =>
    job.draft_columns
      ? natures[0]
      : (resume.trucks?.total || 0) >= (resume.drivers?.total || 0)
        ? 'trucks'
        : 'drivers',
  );
  const [etat, setEtat] = useState('');
  const [recherche, setRecherche] = useState('');
  const [page, setPage] = useState(1);
  const [taille, setTaille] = useState(TAILLE);
  const [donnees, setDonnees] = useState({ items: [], total: 0 });
  const [chargement, setChargement] = useState(true);
  const [occupe, setOccupe] = useState(false);
  const rechercheLente = useDebouncedValue(recherche, 300);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      setDonnees(await api.rows(job.id, { kind: nature, state: etat, q: rechercheLente, page, pageSize: taille }));
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de lire le brouillon'));
    } finally {
      setChargement(false);
    }
  }, [api, job.id, nature, etat, rechercheLente, page, taille]);

  useEffect(() => {
    charger();
  }, [charger, job.draft_version]);

  useEffect(() => {
    setPage(1);
  }, [nature, etat, rechercheLente]);

  const appliquer = async (action, message) => {
    setOccupe(true);
    try {
      onJob(await action());
    } catch (erreur) {
      toast.error(messageErreur(erreur, message));
    } finally {
      setOccupe(false);
    }
  };

  const corriger = (ligne, cle, valeur) =>
    appliquer(() => api.editRow(job.id, ligne.key, { values: { [cle]: valeur === '' ? '' : valeur } }), 'Impossible de corriger la cellule');
  const cocher = (ligne, coche) =>
    appliquer(() => api.editRow(job.id, ligne.key, { included: Boolean(coche) }), 'Impossible de modifier la ligne');

  const colonnes = colonnesDe(job, nature);
  const bloque = disabled || occupe;
  const nombrePages = Math.max(1, Math.ceil((donnees.total || 0) / taille));
  const pagination = {
    page,
    nombrePages,
    taille,
    total: donnees.total || 0,
    premiere: (page - 1) * taille + 1,
    derniere: Math.min(page * taille, donnees.total || 0),
    allerA: (cible) => setPage(Math.min(Math.max(1, cible), nombrePages)),
    changerTaille: (nouvelle) => {
      setTaille(nouvelle);
      setPage(1);
    },
  };
  const compteurs = resume[nature] || {};

  return (
    <section className="space-y-3" data-testid="fleet-import-draft" aria-label="Brouillon">
      <div className="flex flex-wrap items-center gap-2">
        {natures.length > 1 && (
          <>
            {natures.map((cle) => (
              <Button
                key={cle}
                type="button"
                size="sm"
                variant={nature === cle ? 'default' : 'outline'}
                aria-pressed={nature === cle}
                onClick={() => setNature(cle)}
                data-testid={`fleet-import-tab-${cle}`}
              >
                {job.kind_labels?.[cle] || NATURES_LIBELLES[cle] || cle} {resume[cle]?.total || 0}
              </Button>
            ))}
            <span className="mx-1 h-5 w-px bg-border" aria-hidden="true" />
          </>
        )}
        {[
          { cle: '', titre: 'Tous' },
          { cle: 'ready', titre: 'Prêtes', nombre: compteurs.ready },
          { cle: 'review', titre: 'À vérifier', nombre: compteurs.review },
          { cle: 'refused', titre: 'Refusées', nombre: compteurs.refused },
          ...(job.draft_columns ? [{ cle: 'in_lot', titre: 'Déjà dans le lot', nombre: compteurs.in_lot }] : []),
        ].map((filtre) => (
          <Button
            key={filtre.cle || 'tous'}
            type="button"
            size="sm"
            variant={etat === filtre.cle ? 'secondary' : 'ghost'}
            aria-pressed={etat === filtre.cle}
            onClick={() => setEtat(filtre.cle)}
            data-testid={`fleet-import-filter-${filtre.cle || 'all'}`}
          >
            {filtre.titre}
            {filtre.nombre !== undefined ? ` ${filtre.nombre}` : ''}
          </Button>
        ))}
        <div className="relative ml-auto w-full sm:w-56">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/70" aria-hidden="true" />
          <Input
            value={recherche}
            onChange={(event) => setRecherche(event.target.value)}
            placeholder="Rechercher…"
            aria-label="Rechercher dans le brouillon"
            className="h-8 pl-9"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={bloque}
          onClick={() => appliquer(() => api.select(job.id, { included: true, kind: nature, state: 'ready' }), 'Impossible de cocher les lignes')}
          data-testid="fleet-import-check-ready"
        >
          Cocher les lignes prêtes
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={bloque}
          onClick={() => appliquer(() => api.select(job.id, { included: true, kind: nature, state: 'review' }), 'Impossible de cocher les lignes')}
        >
          Cocher les lignes à vérifier
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={bloque}
          onClick={() => appliquer(() => api.select(job.id, { included: false, kind: nature }), 'Impossible de décocher les lignes')}
        >
          Tout décocher
        </Button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-sm" aria-busy={chargement}>
        <table className="w-full border-collapse text-sm [&_td]:border-b [&_td]:border-border/60 [&_td]:px-2 [&_td]:py-1.5 [&_th]:whitespace-nowrap [&_th]:border-b [&_th]:border-border [&_th]:px-2 [&_th]:py-1.5 [&_th]:text-xs [&_th]:font-semibold [&_th]:text-muted-foreground">
          <thead>
            <tr>
              <th className="w-8" aria-label="Importer" />
              <th className="text-right">Ligne</th>
              <th className="text-left">État</th>
              {colonnes.map((colonne) => (
                <th key={colonne.cle} className={colonne.droite ? 'text-right' : 'text-left'}>
                  {colonne.titre}
                </th>
              ))}
              <th className="text-left">Remarque</th>
            </tr>
          </thead>
          <tbody>
            {donnees.items.map((ligne) => (
              <tr
                key={ligne.key}
                className={
                  ligne.state === 'refused'
                    ? 'bg-destructive/5'
                    : ligne.state === 'review'
                      ? 'bg-flame/5'
                      : ligne.state === 'in_lot'
                        ? 'bg-muted/40'
                        : undefined
                }
                data-testid={`fleet-import-row-${ligne.key}`}
              >
                <td>
                  <Checkbox
                    checked={Boolean(ligne.included)}
                    disabled={bloque || NON_COCHABLES.includes(ligne.state)}
                    onCheckedChange={(valeur) => cocher(ligne, valeur)}
                    aria-label={`Importer la ligne ${ligne.row}`}
                  />
                </td>
                <td className="text-right tabular-nums">{ligne.row}</td>
                <td>
                  <StatutBadge domaine="import_flotte_ligne" code={ligne.state} />
                </td>
                {colonnes.map((colonne) => (
                  <td key={colonne.cle}>
                    <CelluleEditable
                      valeur={colonne.valeur(ligne.values)}
                      affichage={colonne.affichage ? colonne.affichage(ligne.values) : undefined}
                      droite={colonne.droite}
                      disabled={bloque || cellulesFigees(ligne)}
                      titre={`${colonne.titre}, ligne ${ligne.row}`}
                      onSave={(valeur) => corriger(ligne, colonne.cle, valeur)}
                    />
                  </td>
                ))}
                <td className="min-w-[14rem]">{ligne.message || '—'}</td>
              </tr>
            ))}
            {!chargement && donnees.items.length === 0 && (
              <tr>
                <td colSpan={colonnes.length + 4} className="py-6 text-center">
                  Aucune ligne
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <BarrePagination pagination={pagination} nom="lignes" />
    </section>
  );
};

export default TableauBrouillon;
